import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cx from 'classnames/bind';
import Input from '../Input/Input';
import PopupMenu from '../PopupMenu/PopupMenu';
import Timepicker from '../Timepicker/Timepicker';
import Gravity from '../../constants/Gravity';
import { isMomentPropType } from '../../lib/customPropTypes';
import { timezoneToOffset } from '../../lib/dateUtils';

/**
 * `TimeInput` is an extension of the HTML `input` element which allows the user to enter or select a time.
 *
 * Takes a moment object as prop.
 * The selected prop must be updated (with ex state) to update the TimeInput
 * NB! See the Timepicker component and Input for more accepted props.
 *
 * @property {boolean} disabled The input field is read only and the calendar is disabled
 * @property {node} errorMessage An error {@link Notice} that will be shown below the component. Defaults to no Notice.
 * @property {string} gravity The popups gravity. See PopupMenu with values
 * @property {node} infoMessage An info Notice that will be shown below the component. Defaults to no Notice.
 * @property {boolean} isClearable Enables a clear button in the input field. When clicked the date is set to NULL
 * @property {boolean} locked The input field will be disabled and show a small lock icon to the right of the input field.
 * @property {function} onChange Returns the selected moment object
 * @property {function} onClearNotice Callback that is called when the user dismiss the optional Notice.
 * @property {boolean} readOnly The input field is read only, but you can select a date from the calendar
 * @property {node} successMessage A success Notice that will be shown below the component. Defaults to no Notice.
 * @property {node} warningMessage A warning Notice that will be shown below the component. Defaults to no Notice.
 *
 * @see Timepicker, Notice, PopupMenu
 */
export default class TimeInput extends Component {

  static displayName = 'TimeInput';

  static propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.node,
    errors: PropTypes.object,
    gravity: PropTypes.string,
    hour24: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    infoMessage: PropTypes.node,
    isClearable: PropTypes.bool,
    label: PropTypes.string,
    locale: PropTypes.string,
    locked: PropTypes.bool,
    onBlur: PropTypes.func,
    onCancel: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    onClearNotice: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    selected: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
      isMomentPropType,
    ]),
    showConfirm: PropTypes.bool,
    showIcon: PropTypes.bool,
    successMessage: PropTypes.node,
    timeFormat: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    utcOffset: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number]
      ),
    warningMessage: PropTypes.node,
  };

  static defaultProps = {
    timeFormat: 'LTS',
    gravity: Gravity.SOUTH,
    locale: moment().locale(),
    utcOffset: moment.utc().utcOffset()
  };

  static formatDate(props, selected) {
    return selected ?
      selected
        .locale(props.locale || moment.locale()).utcOffset(timezoneToOffset(props.utcOffset))
        .format(props.timeFormat) : '';
  }

  constructor(props, context) {
    super(props, context);
    this.onChangeTime = this.onChangeTime.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onInputClick = this.onInputClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.validate = this.validate.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onCloseMenu = this.onCloseMenu.bind(this);

    const selected = props.selected && moment(props.selected); // props.selected might not be moment, so we re-instantiate it
    this.state = {
      value: TimeInput.formatDate(this.props, selected),
      validInput: true,
      anchor: null,
      offset: 0,
      arrowSize: 10,
      isFocused: false,
      selected,
    };
  }

  requiredPropsChanged(prevProps) {
    return (
      this.props.locale !== prevProps.locale ||
      this.props.timeFormat !== prevProps.timeFormat ||
      this.props.utcOffset !== prevProps.utcOffset ||
      moment(this.props.selected).toISOString() !== moment(prevProps.selected).toISOString()
    );
  }
  
  componentDidUpdate(prevProps) {
    if (!this.state.isFocused && this.requiredPropsChanged(prevProps)) {
      const newSelected = this.props.selected && moment(this.props.selected);
      this.setState({
        value: TimeInput.formatDate(this.props, newSelected),
        selected: newSelected,
      });
    }
  }

  onFocus(e) {
    this.setState({ isFocused: true });
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }

  onBlur(e) {
    this.setState({ isFocused: false });
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  }

  onCancel() {
    this.setActive(false);
    if (this.props.onCancel) {
      this.props.onCancel();
    }
  }

  onChangeTime(date) {
    if (this.props.onChange) {
      this.props.onChange(date);
    }
  }

  onCloseMenu() {
    this.setState({ anchor: null });
  }

  onInputClick = (event) => {
    this.setState({
      anchor: event.target,
    });
    if (this.props.onClick) {
      this.props.onClick(event);
    }
  };

  onInputChange(event) {
    if (event.type === 'clear') {
      this.onChangeTime(null);
    } else if (!event.isDefaultPrevented()) {
      let value = event.target.value;
      if (this.props.hour24) {
        value = value.trim();
      } else {
        value = /(A|P){1}M?$/gi.test(value.trim()) ? value.trim() : value;
      }
      let validInput = false;
      if (value === '') {
        validInput = true;
        this.onChangeTime(null);
      } else if (this.validate(value)) {
        validInput = true;
        const date = moment(value,
          ['LT', 'LTS', 'hh:mm:ss A', 'HH:mm:ss', 'HH:mm', 'hh:mm A', 'h:mm:ss A', 'H:mm:ss', 'H:mm', 'h:mm A'], true);
        const selectedDate = this.state.selected || moment.utc().utcOffset(timezoneToOffset(this.props.utcOffset));
        const timezoneDate = selectedDate.clone()
          .hours(date.hours())
          .minutes(date.minutes())
          .seconds(date.seconds());
        this.onChangeTime(timezoneDate);
      }
      this.setState({ value, validInput });
    }
  }

  onChange(date) {
    if (this.props.showConfirm) {
      this.setState({ anchor: null });
    }
    if (this.props.onChange) {
      this.props.onChange(date);
    }
  }

  validate = (str) => {
    if (str !== '') {
      const formats = this.props.hour24 ? ['HH:mm:ss', 'HH:mm', 'H:mm:ss', 'H:mm'] : ['hh:mm:ss A', 'hh:mm A', 'h:mm:ss A', 'h:mm A'];
      return moment(str.trim(), formats, true).isValid();
    }
    return false;
  };

  render() {
    const { anchor, position, offset, arrowSize } = this.state;
    const cls = cx('core-timeinput', this.props.className);
    const cn = cx('timeinput-wrapper', { 'timeinput-wrapper--invalid': !this.state.validInput });
    let popup;

    const input = (
      <div className={cn}>
        <Input
          ref={(c) => { this.timeInput = c; }}
          tabIndex="0"
          className="timeinput-field"
          errors={this.props.errors}
          disabled={this.props.disabled}
          readOnly={this.props.readOnly}
          errorMessage = {this.props.errorMessage}
          infoMessage = {this.props.infoMessage}
          successMessage = {this.props.successMessage}
          warningMessage = {this.props.warningMessage}
          label={this.props.label}
          locked={this.props.locked}
          value={this.state.value}
          onBlur={this.onBlur}
          onClearNotice={this.props.onClearNotice}
          onFocus={this.onFocus}
          onChange={this.onInputChange}
          onClick={this.onInputClick}
          placeholder={this.props.placeholder}
          required={this.props.required}
          showClear={this.props.isClearable}
          clearable={this.props.isClearable}
          iconName={this.props.showIcon ? 'time' : null}/>
      </div>
    );
    if (anchor || position) {
      const timepicker = (
        <div data-preventmenuclose><Timepicker
          {...this.props}
          onCancel={this.onCancel}
          onSelect={this.onChange}
          selected={this.state.selected}
          isClearable={false}/>
        </div>);

      popup = (
        <PopupMenu anchor={anchor} gravity={this.props.gravity}
          offset={offset} arrowSize={arrowSize} customContent={true} onClose={this.onCloseMenu}>
          {timepicker}
        </PopupMenu>
      );
    }

    return (
      <div className={cls}>
        {input}
        {popup}
      </div>
    );
  }

}
